import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import { MY_CONNECTION_PUBLISHES, SEND_CLIP_TO_TIKTOK_DRAFTS } from '@/queries/publishing';
import { CREATE_MY_SCHEDULED_PUBLISH } from '@/queries/scheduledPublishing';
import {
  datetimeLocalValueToIso,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { isScheduledAtTooSoon } from '@/lib/publishing/isScheduledAtTooSoon';
import { minScheduleDatetimeLocal } from '@/lib/publishing/minScheduleDatetimeLocal';
import { isSendableConnection } from '@/lib/publishing/resolvePublishEligibility';
import { computePendingQuota, PENDING_LIMIT } from '@/lib/publishing/computePendingQuota';
import { extractPublishErrorCode, mapPublishError } from '@/lib/publishing/mapPublishError';
import type { PublishConnection, PublishDialogTarget, PublishRecord } from '@/lib/publishing/types';
import { PublishWhenControl, type PublishWhen } from '@/components/publishing/PublishWhenControl';
import { useTikTokClipPreview } from '@/hooks/use-tiktok-clip-preview';
import { resolveTikTokClipBlock } from '@/lib/publishing/resolveTikTokClipBlock';
import { TikTokClipBlockNotice, TikTokClipPreview } from '@/components/publishing/TikTokClipPreview';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipTitle: string;
  initialWhen?: PublishWhen;
  initialDatetimeLocal?: string;
  onSent?: (target: PublishDialogTarget) => void;
}

/** TikTok's drafts endpoint accepts no caption, so this dialog never claims to
 *  one-click publish: it sends the video to the user's TikTok drafts and gives
 *  them a caption they copy and paste themselves inside the TikTok app. */
export const SendToTikTokDialog: React.FC<Props> = ({
  open, onOpenChange, clipId, clipTitle, initialWhen, initialDatetimeLocal, onSent,
}) => {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState<string>('');
  const [caption, setCaption] = useState<string>(clipTitle || '');
  const [when, setWhen] = useState<PublishWhen>('now');
  const [datetimeLocal, setDatetimeLocal] = useState('');
  const [whenError, setWhenError] = useState<string | null>(null);
  const targetVersionRef = useRef(0);
  const mountedRef = useRef(false);
  const currentTargetRef = useRef({ open, clipId });
  currentTargetRef.current = { open, clipId };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    targetVersionRef.current += 1;
    setConnectionId('');
    setCaption(clipTitle || '');
    setWhen(initialWhen ?? 'now');
    setDatetimeLocal(initialDatetimeLocal ?? '');
    setWhenError(null);
  }, [clipId, clipTitle, open, initialWhen, initialDatetimeLocal]);

  const { data: connData } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });

  const [send, { loading }] = useMutation(SEND_CLIP_TO_TIKTOK_DRAFTS);
  const [createScheduled, { loading: scheduling }] = useMutation(CREATE_MY_SCHEDULED_PUBLISH);
  const timeZone = viewerTimeZone();
  const minDatetimeLocal = minScheduleDatetimeLocal(new Date(), timeZone);
  const submitting = loading || scheduling;

  const connections: PublishConnection[] = useMemo(
    () => (connData?.mySocialConnections || [])
      .filter((c: PublishConnection) => c.platform === 'tiktok'),
    [connData],
  );
  const sendable = useMemo(() => connections.filter(isSendableConnection), [connections]);

  const selected = connectionId || sendable[0]?.id || '';
  const preview = useTikTokClipPreview({ open, clipId });
  // Drafts never reach creator_info, so only the watermark rule applies here.
  const clipBlock = resolveTikTokClipBlock({ watermark: preview.watermark });

  // Scoped to the SELECTED CONNECTION across every clip, not to `clipId` —
  // the backend enforces TikTok's pending-drafts cap per connection over a
  // rolling 24h across all of the user's clips (sendClipToTikTokDrafts.js),
  // not per clip. Querying by `clipId` alone (the old approach) undercounts
  // whenever a user's pending drafts are spread across different clips, so
  // the quota shown here would let the user click Send only for the backend
  // to reject it.
  const { data: pubData } = useQuery(MY_CONNECTION_PUBLISHES, {
    variables: { socialConnectionId: selected },
    skip: !open || !selected,
    fetchPolicy: 'cache-and-network',
  });

  // No `refetchQueries` here: this app's client-wide `fetchPolicy: 'no-cache'`
  // (src/lib/apollo-client.ts) excludes MY_CONNECTION_PUBLISHES from that
  // matching entirely, and the page-level query (MY_CLIP_PUBLISHES, keyed by
  // clip ids) is a different operation anyway. The caller's `onSent` — wired
  // to `useClipPublishing`'s `refetchPublishes` — is what actually refreshes
  // the page's status badge after a successful send.
  const quota = useMemo(
    () => computePendingQuota({
      publishes: (pubData?.myConnectionPublishes || []) as PublishRecord[],
      connectionId: selected,
      now: new Date(),
    }),
    [pubData, selected],
  );

  const close = () => {
    targetVersionRef.current += 1;
    onOpenChange(false);
  };

  const goToSettings = () => {
    close();
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform: 'tiktok' },
    }));
  };

  const handleSend = async () => {
    if (clipBlock) return;
    const submittedClipId = clipId;
    const submittedVersion = targetVersionRef.current;
    const submittedTarget: PublishDialogTarget = {
      clipId: submittedClipId,
      platform: 'tiktok',
    };
    const isCurrentSubmission = () => (
      mountedRef.current
      && submittedVersion === targetVersionRef.current
      && currentTargetRef.current.open
      && currentTargetRef.current.clipId === submittedClipId
    );

    try {
      if (when === 'later') {
        const scheduledAt = datetimeLocalValueToIso(datetimeLocal, timeZone);
        if (!scheduledAt || isScheduledAtTooSoon(scheduledAt, new Date())) {
          setWhenError(t('publishing.schedule.tooSoon'));
          return;
        }
        await createScheduled({
          variables: {
            input: {
              clipId: submittedClipId,
              socialConnectionId: selected,
              platform: 'tiktok',
              scheduledAt,
              caption: caption || null,
              title: null,
              description: null,
              privacyStatus: null,
              madeForKids: null,
            },
          },
        });
        if (!isCurrentSubmission()) return;
        toast.success(t('publishing.schedule.saved'));
        close();
        return;
      }

      const result = await send({
        variables: {
          clipId: submittedClipId,
          socialConnectionId: selected,
          caption: caption || null,
        },
      });
      const record = result.data?.sendClipToTikTokDrafts;
      const accepted = !!record && record.status !== 'failed';
      const submissionWasCurrent = isCurrentSubmission();
      if (accepted) onSent?.(submittedTarget);
      if (!submissionWasCurrent) return;

      if (record?.status === 'failed') {
        toast.error(t(mapPublishError(record.fail_reason)));
        return;
      }
      toast.success(t('publishing.sent'));
      close();
    } catch (err) {
      if (!isCurrentSubmission()) return;
      toast.error(t(mapPublishError(extractPublishErrorCode(err))));
    }
  };

  const body = () => {
    if (connections.length === 0) {
      return (
        <div className="space-y-3" data-testid="publish-no-connection">
          <p className="text-sm text-muted-foreground">{t('publishing.noConnectionBody')}</p>
          <Button onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }
    if (sendable.length === 0) {
      return (
        <div className="space-y-3" data-testid="publish-reconnect">
          <p className="text-sm text-muted-foreground">{t('publishing.reconnectBody')}</p>
          <Button onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium">{t('publishing.accountLabel')}</label>
          <Select value={selected} onValueChange={setConnectionId}>
            <SelectTrigger data-testid="publish-account-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sendable.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.display_name || c.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TikTokClipPreview preview={preview} />
        <TikTokClipBlockNotice block={clipBlock} />

        <div className="space-y-1.5">
          <label className="text-xs font-medium">{t('publishing.captionLabel')}</label>
          <Textarea
            data-testid="publish-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={3}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{t('publishing.captionHelp')}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                navigator.clipboard?.writeText(caption);
                toast.success(t('publishing.captionCopied'));
              }}
            >
              <Copy className="mr-1 h-3 w-3" />
              {t('publishing.copyCaption')}
            </Button>
          </div>
        </div>

        {quota.exceeded ? (
          <p className="text-sm text-destructive" data-testid="publish-quota-exceeded">
            {t('publishing.quotaExceededBody')}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('publishing.quotaRemaining', { remaining: quota.remaining, limit: PENDING_LIMIT })}
          </p>
        )}

        <PublishWhenControl
          idPrefix="tiktok"
          when={when}
          onWhenChange={(next) => {
            setWhen(next);
            setWhenError(null);
            if (next === 'later' && !datetimeLocal) setDatetimeLocal(minDatetimeLocal);
          }}
          datetimeLocal={datetimeLocal}
          onDatetimeLocalChange={(value) => {
            setDatetimeLocal(value);
            setWhenError(null);
          }}
          minDatetimeLocal={minDatetimeLocal}
          error={whenError}
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) close();
    }}>
      <DialogContent data-testid="send-tiktok-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('publishing.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('publishing.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {body()}
        {sendable.length > 0 && (
          <DialogFooter>
            <Button
              data-testid="publish-send"
              onClick={handleSend}
              disabled={
                submitting
                || !selected
                || !!clipBlock
                || (when === 'now' && quota.exceeded)
                || (when === 'later' && !datetimeLocal)
              }
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitting ? t('publishing.sending') : t('publishing.send')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendToTikTokDialog;
